/**
 * Manual Floor Tool
 *
 * Allows users to manually place floor planes with a single click.
 * After placing the floor plane, displays a vertical line with height annotation and arrows.
 */

import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { BaseTool } from '../../BaseTool';
import { ToolSettingsSchema, ToolSettingsChangeEvent } from '../../ToolSettings';
import { metersToDisplay, displayToMeters } from '../modules/line-detection/distanceUnits';
import { t } from '../../../translation/markers';
import { createMultiViewConfig, ViewModes } from '../../helpers';
import { PointoramaPlane } from '../../../utils/PointoramaPlane';
import { PlaneCreator } from '../modules/PlaneCreator';
import { FloorBarsPanelController } from '../modules/FloorBarsPanelController';
import { FloorPreviewEditRig, FloorRigFace, FACE_REPICK_EXTRUDE_M } from '../modules/FloorPreviewEditRig';
import { loadFloorFootprintCorners } from '../../../utils/FloorIsolation';
import { FloorplanPersona } from '../../personas/implementations/FloorplanPersona';
import { DetectedFloorPlane } from '../../../utils/floorDetection';
import { getFloorPlanePositionProperty, getCeilingPlanePositionProperty, getFloorFootprintCornerProperty, getGlobalPropertyService, hasGlobalPropertyService, loadFloorDetectionArea, SLAB_SURFACE_MARGIN_M } from '@pointorama/potree-shared';
import type { ReactiveProperty, PropertyValueData } from '@pointorama/potree-shared';
import type { DragEventData } from '../../BaseTool';
import { DefaultColors } from '../../../defines';
import { CameraCullSphere } from '../modules/CameraCullSphere';
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { TextSprite } from '../../../TextSprite';
import { Utils } from '../../../utils';

interface PlaneData {
  position: THREE.Vector3;
  normal: THREE.Vector3;
  height: number;
}

/** Camera-cull radius slider bounds + granularity, in meters. */
const CULL_RADIUS_MIN_M = 1;
const CULL_RADIUS_MAX_M = 20;
const CULL_RADIUS_STEP_M = 0.5;

export class ManualFloorTool extends BaseTool {
  readonly name = 'manual_floor';

  private firstPlaneData: PlaneData | null = null;
  private secondPlaneData: PlaneData | null = null;

  // Keep track of first plane object so we can keep it visible until floor group is created
  private firstPlaneObject: PointoramaPlane | null = null;

  // Plane creation infrastructure (matching PlanePersonaTool)
  private scene: THREE.Scene;
  private activePlane: PointoramaPlane;
  private planeCreator: PlaneCreator;
  private mouse: THREE.Vector2 = new THREE.Vector2(0, 0);
  private shiftDown = false;

  // Vertical line visualization
  private verticalLine: Line2 | null = null;
  private heightLabel: TextSprite | null = null;
  private topArrow: THREE.ArrowHelper | null = null;
  private bottomArrow: THREE.ArrowHelper | null = null;

  // Unregister handle for the floor preview overlay scene
  private unregisterFloorSceneOverlay: (() => void) | null = null;

  // Viewer 'update' listener that rescales the height label each frame.
  private _boundLabelScaleListener: (() => void) | null = null;

  // Floors elevation map — shows where existing floors sit while drawing new ones
  private floorBarsPanel: FloorBarsPanelController | null = null;

  // Camera cull: hide the cloud within a radius of the camera so nearby
  // points don't occlude the view into the scan. Active while placing; a
  // highlighted (selected) floor turns it off — its box isolation is the
  // view aid there — except during a face repick, where looking into the
  // rooms needs the bubble again.
  private cullEnabled = true;
  private repickActive = false;
  private readonly cameraCull = new CameraCullSphere(this.viewer);

  // Edit rig on the SELECTED floor's box (resize arrows + plane repick).
  // Selection also highlights (outline) and box-isolates the floor.
  private editRig: FloorPreviewEditRig | null = null;
  private editGroupId: string | null = null;
  private editFloor: DetectedFloorPlane | null = null;
  private editCeiling: DetectedFloorPlane | null = null;
  private editCorners: THREE.Vector2[] | null = null;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);

    // Setup scene (matching PlanePersonaTool constructor)
    this.scene = new THREE.Scene();
    this.scene.name = 'manual_floor_plane_scene';
    this.viewer.inputHandler.registerInteractiveScene(this.scene);

    this.activePlane = this.createActivePlane();
    this.planeCreator = new PlaneCreator(this.viewer, this.activePlane);
    this.scene.add(this.planeCreator);

    // Rescale the height label once per viewer.update tick. We deliberately
    // do NOT use scene.onBeforeRender — under the multi-RenderView pipeline,
    // that callback fires once per view with mid-frame viewport state, and
    // reading the renderer's size at that moment can leak a widget's tiny
    // dimensions into the main view's scale. Matches how LabelText (used by
    // the working angleLabel sibling) handles the same problem.
    this._boundLabelScaleListener = () => {
      this.updateHeightLabelScale(this.viewer.scene.getActiveCamera());
    };
    this.viewer.addEventListener('update', this._boundLabelScaleListener);
  }

  private createActivePlane(): PointoramaPlane {
    const newPlane = new PointoramaPlane();
    newPlane.tolerance = 0.01;
    newPlane.visible = false;
    newPlane.color = DefaultColors.OBJECTS_THREE;
    this.scene.add(newPlane);
    return newPlane;
  }

  getSettingsSchema(): ToolSettingsSchema {
    // Radius is stored in meters but presented in the user's display unit.
    const unit = this.viewer.lengthUnitDisplay;
    return {
      cameraCull: {
        type: 'toggle',
        label: t('Cull around camera'),
        description: 'Hide the point cloud near the camera while placing the floor and ceiling',
        defaultValue: this.cullEnabled,
      },
      cullRadius: {
        type: 'slider',
        label: t('Cull radius'),
        description: 'Radius of the hidden area around the camera',
        defaultValue: metersToDisplay(this.cameraCull.radius, unit),
        min: metersToDisplay(CULL_RADIUS_MIN_M, unit),
        max: metersToDisplay(CULL_RADIUS_MAX_M, unit),
        step: metersToDisplay(CULL_RADIUS_STEP_M, unit),
        unit: unit.code,
        decimals: 1,
      },
    };
  }

  onSettingsChange(event: ToolSettingsChangeEvent): void {
    if (event.key === 'cameraCull') {
      this.cullEnabled = event.value as boolean;
      this.syncCameraCull();
    } else if (event.key === 'cullRadius') {
      this.cameraCull.radius = displayToMeters(event.value as number, this.viewer.lengthUnitDisplay);
    }
  }

  /** The cull applies while placing planes; a highlighted floor's box
   *  isolation replaces it, except during a face repick. */
  private syncCameraCull(): void {
    if (this.cullEnabled && (!this.editGroupId || this.repickActive)) {
      this.cameraCull.enable();
    } else {
      this.cameraCull.disable();
    }
  }

  /** Rebuild the settings panel when the display unit changes so the radius
   *  slider re-scales to the new unit live. */
  private readonly handleUnitChanged = (): void => {
    this.getSettingsPanel()?.rebuild(this.getSettingsSchema());
  };

  protected onEnable(): void {
    // Register the floor preview scene as a tool overlay so it draws with
    // each view's own camera (main + widgets) via the pipeline.
    this.unregisterFloorSceneOverlay = this.viewer.registerToolOverlayScene(this.scene);

    this.viewer.addEventListener('length_unit_changed', this.handleUnitChanged);
    this.syncCameraCull();

    // Floor tooling proper: the elevation-map panel opens by default here
    // (selection tools start it minimized).
    this.floorBarsPanel ??= new FloorBarsPanelController(this.viewer, this.persona, { defaultCollapsed: false });
    this.floorBarsPanel.enable();

    // Edit rig + isolation on the selected floor
    this.persona.addEventListener('floor_selection_changed', this.handleFloorSelectionForRig);
    this.refreshEditRig();

    this.dispatchStateChanged({ phase: 'placing_floor' });
  }

  protected onDisable(): void {
    this.unregisterFloorSceneOverlay?.();
    this.unregisterFloorSceneOverlay = null;

    this.viewer.removeEventListener('length_unit_changed', this.handleUnitChanged);
    this.cameraCull.disable();

    this.floorBarsPanel?.disable();

    this.persona.removeEventListener('floor_selection_changed', this.handleFloorSelectionForRig);
    this.editRig?.hide();
    this.viewer.floorIsolationManager.clearCustomStrategy();

    // Cleanup (matching PlanePersonaTool.deactivate)
    this.activePlane.visible = false;
    this.activePlane.unHighlight();
    this.planeCreator.angleLabel.visible = false;
    this.planeCreator.reset();

    // Clean up first plane if it exists
    if (this.firstPlaneObject) {
      this.scene.remove(this.firstPlaneObject);
      this.firstPlaneObject = null;
    }

    // Clean up vertical line visualization
    this.removeVerticalLineVisualization();

    // Clean up temporary plane data
    this.firstPlaneData = null;
    this.secondPlaneData = null;
  }

  // ─── Selected-floor edit rig (resize arrows + plane repick) ───────────

  private handleFloorSelectionForRig = (): void => {
    this.refreshEditRig();
  };

  /** Rebuild the rig + highlight + isolation from the persona's selected floor. */
  private refreshEditRig(): void {
    const persona = this.persona as Partial<FloorplanPersona>;
    const groupId = typeof persona.getSelectedFloorGroupIdentifier === 'function' ? persona.getSelectedFloorGroupIdentifier() : null;
    const data = typeof persona.getSelectedFloorData === 'function' ? persona.getSelectedFloorData() : null;

    if (!groupId || !data?.floor || !data.ceiling) {
      this.clearEditState();
      return;
    }

    this.editGroupId = groupId;
    this.editFloor = data.floor;
    this.editCeiling = data.ceiling;
    this.editCorners = this.resolveFootprintCorners(groupId, data.floor);

    this.editRig ??= new FloorPreviewEditRig(this.viewer, {
      onFootprintChanged: (corners, committed) => this.handleRigFootprintChanged(corners, committed),
      onFacePlanePicked: (face, z) => this.handleRigFacePlanePicked(face, z),
      onRepickChanged: (face) => this.handleRigRepickChanged(face),
    });
    this.editRig.show(this.editCorners, data.floor.height, data.ceiling.height, { outline: true });
    this.applySelectedFloorIsolation(null);
    this.syncCameraCull();
  }

  /** Drop rig, highlight and isolation — the unselected resting state. */
  private clearEditState(): void {
    this.editGroupId = null;
    this.editFloor = null;
    this.editCeiling = null;
    this.editCorners = null;
    this.repickActive = false;
    this.editRig?.hide();
    this.viewer.floorIsolationManager.clearCustomStrategy();
    this.syncCameraCull();
  }

  /** Stored per-floor footprint → global detection area → plane bounds. */
  private resolveFootprintCorners(groupId: string, floor: DetectedFloorPlane): THREE.Vector2[] {
    const stored = loadFloorFootprintCorners(groupId);
    if (stored) return stored;
    try {
      const area = loadFloorDetectionArea();
      if (area) return area.map((c) => new THREE.Vector2(c.x, c.y));
    } catch {
      // Property service not ready — fall through to the plane bounds.
    }
    const b = floor.bounds;
    return [new THREE.Vector2(b.min.x, b.min.y), new THREE.Vector2(b.max.x, b.min.y), new THREE.Vector2(b.max.x, b.max.y), new THREE.Vector2(b.min.x, b.max.y)];
  }

  /**
   * Box-isolate the selected floor; a repick face extrudes the box past it.
   * Looking INTO the rooms during a repick is handled by the camera cull
   * sphere (default on), not by section-cutting the box. The z-range gets
   * the same slab-surface margin the room tooling uses — the planes are
   * fitted THROUGH the slab scatter, so an exact clip would cut roughly
   * half the floor/ceiling surface points.
   */
  private applySelectedFloorIsolation(repickFace: FloorRigFace | null): void {
    if (!this.editFloor || !this.editCeiling || !this.editCorners || this.editCorners.length < 4) return;
    const minZ = this.editFloor.height - SLAB_SURFACE_MARGIN_M - (repickFace === 'bottom' ? FACE_REPICK_EXTRUDE_M : 0);
    const maxZ = this.editCeiling.height + SLAB_SURFACE_MARGIN_M + (repickFace === 'top' ? FACE_REPICK_EXTRUDE_M : 0);
    const corners = this.editCorners.slice(0, 4);
    this.viewer.floorIsolationManager.setCustomStrategy({
      kind: 'box',
      xyCorners: [corners[0], corners[1], corners[2], corners[3]],
      minZ,
      maxZ,
    });
  }

  private handleRigFootprintChanged(corners: THREE.Vector2[], committed: boolean): void {
    this.editCorners = corners;
    this.applySelectedFloorIsolation(null);
    // Corners only: writing the (unchanged) plane properties here would fire
    // the persona's plane subscriptions mid-batch, whose refresh re-reads the
    // corners BEFORE the batch stored them — snapping the box back.
    if (committed) this.persistSelectedFloorEdit({ corners: true });
  }

  private handleRigFacePlanePicked(face: FloorRigFace, z: number): void {
    if (!this.editFloor || !this.editCeiling) return;
    const minInterior = 0.5;
    if (face === 'bottom') {
      this.editFloor.height = Math.min(z, this.editCeiling.height - minInterior);
      this.editFloor.center.z = this.editFloor.height;
    } else {
      this.editCeiling.height = Math.max(z, this.editFloor.height + minInterior);
      this.editCeiling.center.z = this.editCeiling.height;
    }
    if (this.editCorners) this.editRig?.show(this.editCorners, this.editFloor.height, this.editCeiling.height, { outline: true });
    this.applySelectedFloorIsolation(null);
    this.persistSelectedFloorEdit({ planes: true });
  }

  private handleRigRepickChanged(face: FloorRigFace | null): void {
    this.applySelectedFloorIsolation(face);
    this.repickActive = face !== null;
    this.syncCameraCull();
    // While repicking, the tool's own placement preview stays hidden.
    if (face) this.activePlane.visible = false;
  }

  /**
   * Persist the edited floor straight to the group's reactive properties —
   * persisting AND broadcasting the change; the persona's plane subscriptions
   * refresh every consumer.
   *
   * Only the requested parts are written: the persona subscribes to the PLANE
   * properties and its refresh fires SYNCHRONOUSLY per property write —
   * writing unchanged planes alongside corners would re-read the corners
   * before the batch stored them and revert the edit. When both parts are
   * written, corners go first for the same reason.
   */
  private persistSelectedFloorEdit(parts: { planes?: boolean; corners?: boolean }): void {
    if (!this.editGroupId || !this.editFloor || !hasGlobalPropertyService()) return;

    type Pair = [ReactiveProperty<PropertyValueData>, PropertyValueData];
    const pairs: Pair[] = [];
    if (parts.corners) {
      this.editCorners?.slice(0, 4).forEach((corner, i) => {
        pairs.push([getFloorFootprintCornerProperty(this.editGroupId as string, i) as unknown as ReactiveProperty<PropertyValueData>, { x: corner.x, y: corner.y, z: this.editFloor?.height ?? 0 }]);
      });
    }
    if (parts.planes) {
      pairs.push([getFloorPlanePositionProperty(this.editGroupId) as unknown as ReactiveProperty<PropertyValueData>, { x: this.editFloor.center.x, y: this.editFloor.center.y, z: this.editFloor.height }]);
      if (this.editCeiling) {
        pairs.push([getCeilingPlanePositionProperty(this.editGroupId) as unknown as ReactiveProperty<PropertyValueData>, { x: this.editCeiling.center.x, y: this.editCeiling.center.y, z: this.editCeiling.height }]);
      }
    }
    if (pairs.length === 0) return;
    void getGlobalPropertyService().batchUpdateProperties(pairs);
  }

  public onMouseDown(event: MouseEvent): void {
    if (event.button !== 0) return;
    this.editRig?.onMouseDown(event);
  }

  public onDragStart(data: DragEventData): void {
    this.editRig?.onDragStart(data);
  }

  public onDrag(data: DragEventData): void {
    this.editRig?.onDrag(data);
  }

  public onDragEnd(data: DragEventData): void {
    this.editRig?.onDragEnd(data);
  }

  /**
   * A face click enters reposition mode when the visible cloud point under
   * the cursor lies NEAR the face's z-plane (the user is clicking that
   * floor/ceiling surface) or when there is none (empty face area). A cloud
   * hit far from the plane means the click aims at a wall / the interior —
   * it stays with the tool's own placement flow.
   */
  private tryEnterFaceRepick(event: MouseEvent): boolean {
    if (!this.editRig || !this.editFloor || !this.editCeiling) return false;

    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    const mousePixel = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
    const camera = this.viewer.scene.getActiveCamera();
    const intersection = Utils.getMousePointCloudIntersection(mousePixel, camera, this.viewer, this.viewer.scene.pointclouds, { pickClipped: false, all: false });
    const cloudZ = intersection && !Array.isArray(intersection) ? intersection.location.z : null;

    const face = this.editRig.resolveFacePick(event, cloudZ);
    if (!face) return false;

    this.editRig.enterRepick(face);
    return true;
  }

  // Event handlers matching PlanePersonaTool
  public onMouseMove(event: MouseEvent): void {
    // Rig first: repick plane preview / handle hover swallow the move so the
    // tool's own placement preview doesn't fight the rig interaction.
    if (this.editRig?.onMouseMove(event)) {
      this.activePlane.visible = false;
      return;
    }

    // Hovering a top/bottom face also pauses the regular placement preview —
    // the rig owns that area.
    if (this.editRig?.pickFace(event)) {
      this.activePlane.visible = false;
      return;
    }

    // Convert to pixel coordinates (NOT normalized) to match what PlanePersonaTool receives
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    this.mouse.x = event.clientX - rect.left;
    this.mouse.y = event.clientY - rect.top;
    this.update();
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Shift') {
      this.shiftDown = true;
      this.update();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      // Exit an active plane repick first; the tool stays active
      if (this.editRig?.cancelRepick()) return;
      // Switch back to default tool (floor_selector)
      this.persona.enableTool('floor_selector');
    }
  }

  public onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Shift') {
      this.shiftDown = false;
      this.update();
    }
  }

  public onClick(event: MouseEvent): void {
    // Rig first: handle release / repick commit / entering a face repick
    if (this.editRig?.onClick(event)) return;
    if (this.tryEnterFaceRepick(event)) return;

    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    this.mouse.x = event.clientX - rect.left;
    this.mouse.y = event.clientY - rect.top;

    // Void click (no visible points under the cursor): deselect everything
    // and reset to the unselected resting state — the tool stays active.
    // Suppressed mid-placement so a missed second click can't throw away the
    // first plane.
    if (!this.firstPlaneData && !this.planeUnderConstruction()) {
      const camera = this.viewer.scene.getActiveCamera();
      const intersection = Utils.getMousePointCloudIntersection(this.mouse, camera, this.viewer, this.viewer.scene.pointclouds, { pickClipped: false, all: false });
      if (!intersection) {
        // Clear locally right away — the deselect_all round-trip through the
        // hierarchy is async, and the rig/isolation must not linger. Floor
        // selection is sticky in the persona, so it needs the EXPLICIT clear.
        this.clearEditState();
        (this.persona as Partial<FloorplanPersona>).clearFloorSelection?.();
        this.viewer.scene.dispatchEvent({ type: 'deselect_all', value: {} });
        return;
      }
    }

    // Left click - add marker and immediately confirm plane
    this.planeCreator.addMarker(this.mouse);

    // Preserve snap state after adding marker
    if (this.shiftDown) {
      this.activePlane.snapInclination();
    }

    // Auto-confirm plane on first click
    this.finishCurrentPlane();
  }

  public onRightClick(event: MouseEvent): void {
    // Right click - remove marker (drag filtering handled by BaseTool)
    event.preventDefault();
    event.stopPropagation();

    // An active plane repick swallows the right-click (exits repick)
    if (this.editRig?.onRightClick(event)) return;

    // Floor plane already placed (waiting for the ceiling click):
    // right-click cancels the pending floor and restarts placement.
    if (this.firstPlaneData) {
      this.cancelPendingPlacement();
      return;
    }

    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    this.mouse.x = event.clientX - rect.left;
    this.mouse.y = event.clientY - rect.top;

    this.planeCreator.removeMarker();
    this.planeCreator.adjustActivePlaneToMousePosition(this.mouse);
  }

  /** Discard the in-progress floor/ceiling placement and return to the
   *  initial placing state — same cleanup as onDisable, but the tool stays on. */
  private cancelPendingPlacement(): void {
    this.activePlane.visible = false;
    this.activePlane.unHighlight();
    this.planeCreator.angleLabel.visible = false;
    this.planeCreator.reset();
    if (this.firstPlaneObject) {
      this.scene.remove(this.firstPlaneObject);
      this.firstPlaneObject = null;
    }
    this.removeVerticalLineVisualization();
    this.firstPlaneData = null;
    this.secondPlaneData = null;
    this.dispatchStateChanged({ phase: 'placing_floor' });
  }

  public onDoubleClick(event: MouseEvent): void {
    // Double-click is no longer used - planes are auto-confirmed on single click
    event.preventDefault();
    event.stopPropagation();
  }

  private finishCurrentPlane(): void {
    if (!this.planeUnderConstruction()) return;

    // Preserve snap state before finishing plane
    if (this.shiftDown) {
      this.activePlane.snapInclination();
    }

    this.activePlane.finish();

    // Get the plane normal
    let normal = this.activePlane.getNormal().clone();

    // Check if the plane is too far from horizontal (>10 degrees)
    const verticalAxis = new THREE.Vector3(0, 0, 1);
    const angle = normal.angleTo(verticalAxis);
    const angleDegrees = (angle * 180) / Math.PI;

    // If more than 10 degrees off horizontal, force it to be horizontal
    if (angleDegrees > 10 && angleDegrees < 170) {
      console.log(`[ManualFloorTool] Floor plane is ${angleDegrees.toFixed(1)}° off horizontal, forcing to horizontal`);
      normal = new THREE.Vector3(0, 0, 1);
    }

    // Store plane data (DIFFERENT from PlanePersonaTool - don't add to scene)
    const planeData: PlaneData = {
      position: this.activePlane.position.clone(),
      normal: normal,
      height: this.activePlane.position.z,
    };

    // Reset plane creator and create new active plane
    this.resetPlaneCreator();
    this.onPlaneFinished(planeData);
  }

  private resetPlaneCreator(): void {
    // Reset the plane creator
    this.planeCreator.reset();

    // For the first plane, keep it visible in the scene
    if (!this.firstPlaneObject) {
      this.firstPlaneObject = this.activePlane;
    } else {
      // For the second plane, remove it (floor group will be created)
      this.scene.remove(this.activePlane);
    }

    // Create new active plane
    this.activePlane = this.createActivePlane();
    this.planeCreator.activePlane = this.activePlane;
  }

  // Update method matching PlanePersonaTool
  private update(): void {
    this.planeCreator.adjustActivePlaneToMousePosition(this.mouse);

    // Note: We skip PlaneTransformer logic as we don't support editing existing planes

    if (this.shiftDown) {
      this.activePlane.snapInclination();
    }

    // Update vertical line visualization if first plane exists
    if (this.firstPlaneData) {
      this.updateVerticalLineVisualization();
    }
  }

  private planeUnderConstruction(): boolean {
    return this.planeCreator.selectedFittingPoints.length > 0;
  }

  // Update label scale based on camera distance (matching Potree polygon labels).
  // Driven by the viewer 'update' event, which fires once per frame before the
  // multi-RenderView render loop swaps the renderer size around.
  private updateHeightLabelScale(camera: THREE.Camera): void {
    if (!this.heightLabel) return;

    const renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
    const clientWidth = renderAreaSize.width;
    const clientHeight = renderAreaSize.height;

    const distance = camera.position.distanceTo(this.heightLabel.getWorldPosition(new THREE.Vector3()));
    const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
    const scale = 70 / pr;
    this.heightLabel.scale.set(scale, scale, scale);
  }

  // Floor-specific logic (DIFFERENT from PlanePersonaTool)
  private onPlaneFinished(planeData: PlaneData): void {
    if (!this.firstPlaneData) {
      // First plane created
      this.firstPlaneData = planeData;

      // Create vertical line visualization from this plane
      this.createVerticalLineVisualization(planeData.position);

      this.dispatchStateChanged({ phase: 'placing_ceiling' });
    } else if (!this.secondPlaneData) {
      // Second plane placed — the z-range is set. The footprint is automatic:
      // the whole point cloud's XY bounding box (replacing the old drawn
      // X-ray rectangle stage). Refinement happens on the selected floor's
      // edit rig.
      this.secondPlaneData = planeData;

      if (this.firstPlaneObject) {
        this.scene.remove(this.firstPlaneObject);
        this.firstPlaneObject = null;
      }
      this.removeVerticalLineVisualization();

      this.createFloorGroup(this.cloudFootprintCorners(this.viewer.scene.getBoundingBox()));
      this.firstPlaneData = null;
      this.secondPlaneData = null;
    }
  }

  /** Footprint covering the entire point cloud's XY bounds (z is stamped
   *  with the floor height by createFloorGroup). Undefined when no cloud is
   *  loaded — the floor then keeps no explicit footprint. */
  private cloudFootprintCorners(bounds: THREE.Box3): [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3] | undefined {
    if (bounds.isEmpty()) return undefined;
    return [new THREE.Vector3(bounds.min.x, bounds.min.y, 0), new THREE.Vector3(bounds.max.x, bounds.min.y, 0), new THREE.Vector3(bounds.max.x, bounds.max.y, 0), new THREE.Vector3(bounds.min.x, bounds.max.y, 0)];
  }

  private createFloorGroup(footprintCorners?: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3]): void {
    if (!this.firstPlaneData || !this.secondPlaneData) {
      return;
    }

    // Determine which plane is floor (lower) and which is ceiling (higher) based on z-height
    let floorPlane: PlaneData;
    let ceilingPlane: PlaneData;

    if (this.firstPlaneData.height < this.secondPlaneData.height) {
      // First plane is lower = floor
      floorPlane = this.firstPlaneData;
      ceilingPlane = this.secondPlaneData;
    } else {
      // Second plane is lower = floor
      floorPlane = this.secondPlaneData;
      ceilingPlane = this.firstPlaneData;
    }

    const floorPosition = floorPlane.position;
    // Force floor normal to point straight up
    const floorNormal = new THREE.Vector3(0, 0, 1);

    const ceilingPosition = ceilingPlane.position;
    // Force ceiling normal to point straight down
    const ceilingNormal = new THREE.Vector3(0, 0, -1);

    // Persist the planes AT the picked surfaces — no baked-in offsets.
    // Clipping slack around the surfaces is applied locally by consumers
    // (LevelBounds 'slabSurface' padding); a persisted offset would skew
    // every derived elevation (wall heights, IFC, panel dimensions) and
    // disagree with rig-edited planes, which commit the exact picked z.
    const floorHeight = floorPlane.height;
    const ceilingHeight = ceilingPlane.height;
    const adjustedFloorPosition = floorPosition.clone();
    const adjustedCeilingPosition = ceilingPosition.clone();

    // Dispatch event to create floor group
    this.viewer.scene.dispatchEvent({
      type: 'create_floor',
      value: {
        floor: {
          height: floorHeight,
          center: adjustedFloorPosition,
          normal: floorNormal,
          pointCount: 0,
          confidence: 1.0,
        },
        ceiling: {
          height: ceilingHeight,
          center: adjustedCeilingPosition,
          normal: ceilingNormal,
          pointCount: 0,
          confidence: 1.0,
        },
        // The floor's OWN bounding area, set once at creation — the whole
        // point cloud's XY bounds (refined later via the edit rig).
        ...(footprintCorners ? { footprint: footprintCorners.map((c) => ({ x: c.x, y: c.y, z: floorHeight })) } : {}),
      },
    });

    // Dispatch notification
    this.viewer.scene.dispatchEvent({
      type: 'floor_added_notification',
      value: {
        floorHeight: floorHeight.toFixed(2),
        floorsRemaining: 0,
      },
    });

    this.dispatchStateChanged({ phase: 'placing_floor' });
  }

  private createVerticalLineVisualization(planePosition: THREE.Vector3): void {
    // Remove existing visualization if any
    this.removeVerticalLineVisualization();

    // Create vertical line (will be updated in render loop)
    const lineGeometry = new LineGeometry();
    const positions = [planePosition.x, planePosition.y, planePosition.z, planePosition.x, planePosition.y, planePosition.z];
    lineGeometry.setPositions(positions);

    const lineMaterial = new LineMaterial({
      color: DefaultColors.OBJECTS_THREE,
      linewidth: 2,
      resolution: new THREE.Vector2(1000, 1000),
      depthTest: false,
    });

    this.verticalLine = new Line2(lineGeometry, lineMaterial);
    this.scene.add(this.verticalLine);

    // Create height label (matching Potree polygon label style)
    this.heightLabel = new TextSprite();
    this.heightLabel.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
    this.heightLabel.setBackgroundColor({ r: 0, g: 0, b: 0, a: 0.3 });
    this.heightLabel.fontsize = 16;
    this.heightLabel.material.depthTest = false;
    this.heightLabel.material.opacity = 1;
    this.heightLabel.setText('0.00m');
    this.heightLabel.position.set(planePosition.x, planePosition.y, planePosition.z);
    this.scene.add(this.heightLabel);
    // Update world matrix and scale immediately
    this.heightLabel.updateMatrixWorld(true);
    this.updateHeightLabelScale(this.viewer.scene.getActiveCamera());

    // Create arrows (will be updated in render loop)
    const upDirection = new THREE.Vector3(0, 0, 1);
    const arrowLength = 0.3;
    const arrowColor = DefaultColors.OBJECTS_THREE;
    this.topArrow = new THREE.ArrowHelper(upDirection, new THREE.Vector3(planePosition.x, planePosition.y, planePosition.z), arrowLength, arrowColor, arrowLength * 0.4, arrowLength * 0.3);
    this.scene.add(this.topArrow);

    const downDirection = new THREE.Vector3(0, 0, -1);
    this.bottomArrow = new THREE.ArrowHelper(downDirection, new THREE.Vector3(planePosition.x, planePosition.y, planePosition.z), arrowLength, arrowColor, arrowLength * 0.4, arrowLength * 0.3);
    this.scene.add(this.bottomArrow);
  }

  private updateVerticalLineVisualization(): void {
    if (!this.verticalLine || !this.firstPlaneData) return;

    // Get the preview plane's Z position
    const previewZ = this.activePlane.position.z;
    const firstPlaneZ = this.firstPlaneData.position.z;
    const firstPlaneX = this.firstPlaneData.position.x;
    const firstPlaneY = this.firstPlaneData.position.y;

    // Shorten line by 0.1m on each end to avoid interfering with arrow tips
    const lineOffset = 0.1;
    let startZ: number, endZ: number;

    if (previewZ > firstPlaneZ) {
      // Preview is above first plane
      startZ = firstPlaneZ + lineOffset;
      endZ = previewZ - lineOffset;
    } else {
      // Preview is below first plane
      startZ = firstPlaneZ - lineOffset;
      endZ = previewZ + lineOffset;
    }

    // Update line from first plane to preview plane Z (shortened)
    const startPoint = new THREE.Vector3(firstPlaneX, firstPlaneY, startZ);
    const endPoint = new THREE.Vector3(firstPlaneX, firstPlaneY, endZ);

    const positions = [startPoint.x, startPoint.y, startPoint.z, endPoint.x, endPoint.y, endPoint.z];
    (this.verticalLine.geometry as LineGeometry).setPositions(positions);

    // Calculate height
    const height = Math.abs(previewZ - firstPlaneZ);

    // Update height label
    if (this.heightLabel) {
      this.heightLabel.setText(`${height.toFixed(2)}m`);
      const midZ = (firstPlaneZ + previewZ) / 2;
      this.heightLabel.position.set(firstPlaneX, firstPlaneY, midZ);
      this.heightLabel.updateMatrixWorld(true);
      this.updateHeightLabelScale(this.viewer.scene.getActiveCamera());
    }

    // Update arrows - position the arrow base so the tip ends at the line endpoint
    const arrowLength = 0.3;
    if (previewZ > firstPlaneZ) {
      // Preview is above first plane
      if (this.topArrow) {
        // Arrow points up, so position base below the tip
        this.topArrow.position.set(firstPlaneX, firstPlaneY, previewZ - arrowLength);
        this.topArrow.setDirection(new THREE.Vector3(0, 0, 1));
      }
      if (this.bottomArrow) {
        // Arrow points down, so position base above the tip
        this.bottomArrow.position.set(firstPlaneX, firstPlaneY, firstPlaneZ + arrowLength);
        this.bottomArrow.setDirection(new THREE.Vector3(0, 0, -1));
      }
    } else {
      // Preview is below first plane
      if (this.topArrow) {
        // Arrow points up, so position base below the tip
        this.topArrow.position.set(firstPlaneX, firstPlaneY, firstPlaneZ - arrowLength);
        this.topArrow.setDirection(new THREE.Vector3(0, 0, 1));
      }
      if (this.bottomArrow) {
        // Arrow points down, so position base above the tip
        this.bottomArrow.position.set(firstPlaneX, firstPlaneY, previewZ + arrowLength);
        this.bottomArrow.setDirection(new THREE.Vector3(0, 0, -1));
      }
    }
  }

  private removeVerticalLineVisualization(): void {
    if (this.verticalLine) {
      this.scene.remove(this.verticalLine);
      this.verticalLine.geometry.dispose();
      (this.verticalLine.material as LineMaterial).dispose();
      this.verticalLine = null;
    }

    if (this.heightLabel) {
      this.scene.remove(this.heightLabel);
      this.heightLabel = null;
    }

    if (this.topArrow) {
      this.scene.remove(this.topArrow);
      this.topArrow.dispose();
      this.topArrow = null;
    }

    if (this.bottomArrow) {
      this.scene.remove(this.bottomArrow);
      this.bottomArrow.dispose();
      this.bottomArrow = null;
    }
  }

  getViewConfig(): IViewConfig {
    return createMultiViewConfig([ViewModes.PERSPECTIVE_3D_RGB], ViewModes.PERSPECTIVE_3D_RGB.id);
  }
}
